import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getFirestore, collection, onSnapshot, doc, deleteDoc, query, where } from 'firebase/firestore';
import { onIdTokenChanged } from 'firebase/auth';
import { app, auth } from '../firebase';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

const StatusIndicator = ({ status }) => {
  const isOnline = status === 'online';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: isOnline ? 'success.main' : 'error.main',
        }}
      />
      <Typography variant="body2" color="text.secondary">
        {status}
      </Typography>
    </Box>
  );
};

const db = getFirestore(app);
const DEFAULT_TIMEOUT_SECONDS = 300;

const isSilenceDetectorEnabled = (value) => value !== false;

export default function ParticipantsList() {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [userDomainId, setUserDomainId] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [domains, setDomains] = useState([]);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setIsSystemAdmin(false);
        setUserDomainId(null);
        setAuthChecked(true);
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        setIsSystemAdmin(tokenResult?.claims?.role === 'SYSTEM_ADMIN');
        setUserDomainId(tokenResult?.claims?.domainId || null);
      } catch (e) {
        console.error(e);
      } finally {
        setAuthChecked(true);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!isSystemAdmin) return;
    const unsub = onSnapshot(collection(db, 'domains'), (snap) => {
      setDomains(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [isSystemAdmin]);

  useEffect(() => {
    if (!authChecked) return;

    // For non-System Admins, we MUST have the domainId before we can construct a valid query
    if (!isSystemAdmin && !userDomainId) {
      setLoading(false);
      return;
    }

    let q = query(collection(db, 'participants'));

    // Domain Admins MUST filter by domainId to satisfy security rules
    if (!isSystemAdmin && userDomainId) {
      q = query(collection(db, 'participants'), where('domainId', '==', userDomainId));
    }

    const unsub = onSnapshot(q, (snap) => {
      const items = [];
      snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
      setParticipants(items);
      setLoading(false);
    }, (err) => {
      console.error('participants snapshot error', err);
      setLoading(false);
    });
    return () => unsub();
  }, [authChecked, isSystemAdmin, userDomainId]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete participant?')) return;
    try {
      await deleteDoc(doc(db, 'participants', id));
    } catch (e) {
      console.error('delete participant error', e);
      alert('Failed to delete participant');
    }
  };

  const resolveTimeoutSeconds = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return DEFAULT_TIMEOUT_SECONDS;
  };

  const getDomainName = (id) => {
    const domain = domains.find(d => d.id === id);
    return domain ? domain.name : id;
  };

  if (loading) return <div>Loading participants...</div>;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Participants</Typography>
        <Button variant="contained" onClick={() => navigate('/participants/new')}>Create Participant</Button>
      </Box>
      {participants.length === 0 ? (
        <Typography>No participants found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {isSystemAdmin && <TableCell>Domain</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Call Timeout (s)</TableCell>
                <TableCell>Silence Detector</TableCell>
                <TableCell>Rooms</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {participants.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.name}</TableCell>
                  {isSystemAdmin && <TableCell>{getDomainName(p.domainId)}</TableCell>}
                  <TableCell>
                    <StatusIndicator status={p.status} />
                  </TableCell>
                  <TableCell>{resolveTimeoutSeconds(p.callTimeoutSeconds)}</TableCell>
                  <TableCell>{isSilenceDetectorEnabled(p.silenceDetectorEnabled) ? 'Enabled' : 'Disabled'}</TableCell>
                  <TableCell>{(p.linkedRoomIds || []).join(', ')}</TableCell>
                  <TableCell>
                    <Button component={Link} to={`/participants/${p.id}`} size="small">View</Button>
                    <Button component={Link} to={`/participants/${p.id}/edit`} size="small">Edit</Button>
                    <Button onClick={() => handleDelete(p.id)} size="small" color="error">Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
